import React, { useState, useRef } from 'react';

// Web Audio API Synthesizer for Royal Fanfare Trumpet & Royal Gong (100% Client-side, Zero Cost)
function playRoyalFanfareSound(type: 'trumpet' | 'gong' | 'chime') {
  try {
    const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();

    if (type === 'trumpet') {
      // Royal Trumpet Fanfare sequence (Notes: C4, G4, C5, E5, G5)
      const notes = [261.63, 392.00, 523.25, 659.25, 783.99];
      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(freq, ctx.currentTime + i * 0.15);

        gain.gain.setValueAtTime(0.3, ctx.currentTime + i * 0.15);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.15 + 0.4);

        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime + i * 0.15);
        osc.stop(ctx.currentTime + i * 0.15 + 0.45);
      });
    } else if (type === 'gong') {
      // Deep Royal Palace Gong
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(120, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(60, ctx.currentTime + 1.5);

      gain.gain.setValueAtTime(0.5, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 2.0);

      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 2.1);
    } else if (type === 'chime') {
      // Palace Crystal Notification Chime
      [523.25, 659.25, 783.99, 1046.50].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(freq, ctx.currentTime + i * 0.1);

        gain.gain.setValueAtTime(0.2, ctx.currentTime + i * 0.1);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.1 + 0.3);

        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime + i * 0.1);
        osc.stop(ctx.currentTime + i * 0.1 + 0.35);
      });
    }
  } catch (err) {
    console.error('Audio synthesizer error:', err);
  }
}

interface AudioProclamation {
  id: string;
  title: string;
  speaker: string;
  village: string;
  duration: string;
  date: string;
  category: string;
  transcript: string;
  audioUrl?: string;
}

const PRESET_PROCLAMATIONS: AudioProclamation[] = [
  {
    id: 'proc-1',
    title: 'Opening Royal Blessing & Message for ERUE Festival 2026',
    speaker: 'HRH Ogieneni of Uzairue Kingdom',
    village: 'Palace Grounds, Jattu',
    duration: '02:45',
    date: 'Aug 12, 2026',
    category: 'Royal Decree',
    transcript: 'Peace and grace be unto all Uzairue sons and daughters across Nigeria, Great Britain, America, Europe, and Asia. As we prepare for the sacred ERUE Festival, I urge all 22 villages and communities to unite in brotherhood and support the Grand Raffle Draw to empower our youths.',
  },
  {
    id: 'proc-2',
    title: 'Diaspora Delegates Welcome & Security Briefing',
    speaker: 'Chief Danialu Momoh (Palace Liaison)',
    village: 'Jattu-Uzairue',
    duration: '01:30',
    date: 'Aug 10, 2026',
    category: 'Diaspora Notice',
    transcript: 'To all our brothers and sisters returning from overseas: Edo State security taskforces and royal palace guards have established 24/7 security corridors from Benin City Airport to Uzairue Kingdom. Shuttle services are available.',
  },
  {
    id: 'proc-3',
    title: 'Age-Grade Initiation & Masquerade Protocol',
    speaker: 'Elder Braimah Afegbua',
    village: 'Afashio-Uzairue',
    duration: '02:10',
    date: 'Aug 08, 2026',
    category: 'Cultural Tradition',
    transcript: 'Details regarding the 2026 Age-Grade Initiation ceremonies for young men and women. All village youth leaders are requested to submit lists to the central cultural committee by September 15th.',
  },
];

export const RoyalAudioTool: React.FC = () => {
  const [proclamations, setProclamations] = useState<AudioProclamation[]>(PRESET_PROCLAMATIONS);
  const [activePlayingId, setActivePlayingId] = useState<string | null>(null);

  // Voice Note Recorder state
  const [isRecording, setIsRecording] = useState(false);
  const [recordedAudioUrl, setRecordedAudioUrl] = useState<string | null>(null);
  const [recordingTitle, setRecordingTitle] = useState('');
  const [recordingSpeaker, setRecordingSpeaker] = useState('High Chief Scribe');
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  // Sound Synthesizer triggers
  const handlePlayFanfare = (type: 'trumpet' | 'gong' | 'chime') => {
    playRoyalFanfareSound(type);
  };

  // Start Voice Note Recording
  const handleStartRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(audioBlob);
        setRecordedAudioUrl(url);
      };

      mediaRecorder.start();
      setIsRecording(true);
      playRoyalFanfareSound('chime');
    } catch (err) {
      alert('Microphone access is required to record voice proclamations.');
    }
  };

  // Stop Voice Note Recording
  const handleStopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach((track) => track.stop());
      setIsRecording(false);
      playRoyalFanfareSound('chime');
    }
  };

  // Save Recorded Voice Proclamation
  const handleSaveVoiceProclamation = () => {
    if (!recordingTitle.trim() || !recordedAudioUrl) return;

    const newProc: AudioProclamation = {
      id: `proc-rec-${Date.now()}`,
      title: recordingTitle.trim(),
      speaker: recordingSpeaker.trim() || 'Palace Messenger',
      village: 'Uzairue Kingdom',
      duration: '01:15',
      date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      category: 'Voice Broadcast',
      transcript: `Recorded audio message: "${recordingTitle.trim()}"`,
      audioUrl: recordedAudioUrl,
    };

    setProclamations([newProc, ...proclamations]);
    setRecordingTitle('');
    setRecordedAudioUrl(null);
    playRoyalFanfareSound('trumpet');
  };

  const handleSimulatePlay = (proc: AudioProclamation) => {
    if (activePlayingId === proc.id) {
      setActivePlayingId(null);
      return;
    }
    setActivePlayingId(proc.id);
    playRoyalFanfareSound('gong');
  };

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-8 shadow-2xl space-y-8 text-white">
      {/* Header Banner */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-slate-800 pb-6">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-2xl">🎺</span>
            <h2 className="text-xl md:text-2xl font-serif font-black text-amber-300">
              Royal Voice Proclamations & Town Crier Sound Suite
            </h2>
            <span className="bg-amber-400 text-slate-950 text-[10px] px-2.5 py-0.5 rounded-full font-bold uppercase font-mono">
              Web Audio Synthesizer
            </span>
          </div>
          <p className="text-xs text-slate-300 mt-1">
            Listen to official royal audio decrees, play synthesized palace fanfare trumpets & gong chimes, and record voice note broadcasts for Uzairue clans.
          </p>
        </div>

        {/* Web Audio Chime Triggers */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => handlePlayFanfare('trumpet')}
            className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-black px-3.5 py-2 rounded-xl text-xs uppercase tracking-wider transition-all cursor-pointer flex items-center gap-1.5 shadow-md"
            title="Synthesize Royal Trumpet Fanfare sound"
          >
            <span>🎺 Fanfare Trumpet</span>
          </button>

          <button
            onClick={() => handlePlayFanfare('gong')}
            className="bg-emerald-600 hover:bg-emerald-500 text-white font-black px-3.5 py-2 rounded-xl text-xs uppercase tracking-wider transition-all cursor-pointer flex items-center gap-1.5 shadow-md"
            title="Synthesize Royal Palace Gong sound"
          >
            <span>🔔 Palace Gong</span>
          </button>

          <button
            onClick={() => handlePlayFanfare('chime')}
            className="bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 font-bold px-3.5 py-2 rounded-xl text-xs transition-all cursor-pointer flex items-center gap-1.5"
            title="Synthesize Crystal Chime"
          >
            <span>✨ Crystal Chime</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Left Column: Proclamations Audio List */}
        <div className="lg:col-span-7 space-y-4">
          <h3 className="text-xs font-black uppercase text-amber-400 tracking-wider flex items-center gap-2">
            <span>📜</span> Royal Audio Proclamations Archive ({proclamations.length})
          </h3>

          <div className="space-y-3">
            {proclamations.map((proc) => {
              const isPlaying = activePlayingId === proc.id;
              return (
                <div
                  key={proc.id}
                  className={`p-4 rounded-2xl border transition-all ${
                    isPlaying
                      ? 'bg-emerald-950/80 border-amber-400 shadow-xl'
                      : 'bg-slate-950 border-slate-800 hover:border-slate-700'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="bg-amber-400/20 text-amber-300 text-[10px] font-black px-2 py-0.5 rounded border border-amber-400/30">
                          {proc.category}
                        </span>
                        <span className="text-[10px] text-slate-400 font-mono">{proc.date}</span>
                        <span className="text-[10px] text-slate-400 font-mono">• {proc.duration}</span>
                      </div>
                      <h4 className="text-sm font-serif font-black text-white">{proc.title}</h4>
                      <p className="text-[11px] text-emerald-300 font-bold">
                        🗣️ {proc.speaker} ({proc.village})
                      </p>
                    </div>

                    <button
                      onClick={() => handleSimulatePlay(proc)}
                      className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer flex items-center gap-1.5 ${
                        isPlaying
                          ? 'bg-amber-400 text-slate-950 animate-pulse'
                          : 'bg-slate-800 text-amber-300 hover:bg-slate-700 border border-slate-700'
                      }`}
                    >
                      <span>{isPlaying ? '⏸️ Playing' : '▶️ Listen'}</span>
                    </button>
                  </div>

                  {/* Transcript */}
                  <p className="text-xs text-slate-300 italic bg-slate-900/60 p-3 rounded-xl border border-slate-800/80 mt-3 leading-relaxed">
                    "{proc.transcript}"
                  </p>

                  {/* HTML5 Audio Player if user recorded */}
                  {proc.audioUrl && (
                    <div className="mt-3">
                      <audio controls src={proc.audioUrl} className="w-full h-8" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Column: Voice Note Recorder */}
        <div className="lg:col-span-5 bg-slate-950 border border-slate-800 rounded-3xl p-6 space-y-5">
          <div>
            <span className="text-[10px] font-extrabold uppercase bg-amber-400/20 text-amber-300 px-2.5 py-0.5 rounded border border-amber-400/30">
              Town Crier Recording Studio
            </span>
            <h3 className="text-lg font-serif font-black text-white mt-1">
              Record Voice Proclamation
            </h3>
            <p className="text-xs text-slate-400">
              Record a voice note announcement directly in your browser using your microphone.
            </p>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-[11px] font-bold text-slate-300 mb-1">Speaker / Title Name</label>
              <input
                type="text"
                value={recordingSpeaker}
                onChange={(e) => setRecordingSpeaker(e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:border-amber-400 outline-none"
                placeholder="e.g. High Chief Dr. Aliu Momoh"
              />
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-300 mb-1">Proclamation Headline</label>
              <input
                type="text"
                value={recordingTitle}
                onChange={(e) => setRecordingTitle(e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:border-amber-400 outline-none font-bold"
                placeholder="e.g. Youth Age-Grade Assembly Notice"
              />
            </div>
          </div>

          {/* Controls */}
          <div className="bg-slate-900 p-5 rounded-2xl border border-slate-800 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-slate-800 border-2 border-amber-400 flex items-center justify-center mx-auto text-2xl shadow-lg">
              {isRecording ? <span className="animate-ping">🔴</span> : <span>🎙️</span>}
            </div>

            <p className="text-xs text-slate-300 font-mono">
              {isRecording ? '🔴 Recording in progress... Speak clearly.' : 'Click below to start recording voice note.'}
            </p>

            <div className="flex items-center justify-center gap-3">
              {!isRecording ? (
                <button
                  onClick={handleStartRecording}
                  className="bg-red-600 hover:bg-red-500 text-white font-extrabold px-5 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all cursor-pointer shadow-lg"
                >
                  🎙️ Start Recording
                </button>
              ) : (
                <button
                  onClick={handleStopRecording}
                  className="bg-amber-400 hover:bg-amber-300 text-slate-950 font-extrabold px-5 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all cursor-pointer shadow-lg animate-bounce"
                >
                  ⏹️ Stop Recording
                </button>
              )}
            </div>

            {recordedAudioUrl && (
              <div className="pt-3 border-t border-slate-800 space-y-3">
                <p className="text-xs text-emerald-400 font-bold">✅ Voice note recorded successfully!</p>
                <audio controls src={recordedAudioUrl} className="w-full h-8" />
                <button
                  onClick={handleSaveVoiceProclamation}
                  disabled={!recordingTitle.trim()}
                  className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold px-4 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all cursor-pointer"
                >
                  💾 Save & Publish Proclamation
                </button>
              </div>
            )}
          </div>

        </div>

      </div>
    </div>
  );
};

export default RoyalAudioTool;
